import type { Store } from "../storage";

type Box = { iv: Uint8Array<ArrayBuffer>; data: ArrayBuffer };

/**
 * Keeps values encrypted with AES-256-GCM inside another store. The key is a non-extractable
 * WebCrypto key kept in the same database, so the values cannot be read by copying the database
 * files off the device. Signing out deletes the database, key and all.
 */
export class SealedStore {
  private key: Promise<CryptoKey> | null = null;

  constructor(
    private readonly inner: Store,
    private readonly prefix = "sealed:",
  ) {}

  private getKey(): Promise<CryptoKey> {
    this.key ??= (async () => {
      const existing = await this.inner.get<CryptoKey>(`${this.prefix}key`);
      if (existing) return existing;
      const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, [
        "encrypt",
        "decrypt",
      ]);
      await this.inner.set(`${this.prefix}key`, key);
      return key;
    })();
    return this.key;
  }

  async get<T>(key: string): Promise<T | undefined> {
    const box = await this.inner.get<Box>(`${this.prefix}v:${key}`);
    if (!box) return undefined;
    try {
      const plain = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: box.iv },
        await this.getKey(),
        box.data,
      );
      return JSON.parse(new TextDecoder().decode(plain)) as T;
    } catch {
      return undefined;
    }
  }

  async set(key: string, value: unknown): Promise<void> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      await this.getKey(),
      new TextEncoder().encode(JSON.stringify(value)),
    );
    await this.inner.set(`${this.prefix}v:${key}`, { iv, data } satisfies Box);
  }

  async delete(key: string): Promise<void> {
    await this.inner.delete(`${this.prefix}v:${key}`);
  }
}
